import { CheckCircle2, Loader2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { lpConfig } from "../lpConfig";
import { useFadeIn } from "../useFadeIn";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";

type FormData = {
  companyName: string;
  name: string;
  email: string;
  phone: string;
  teamSize: string;
};

/**
 * ランディングページのCTAフォームセクション
 * 無料トライアル申し込みフォームと信頼項目を表示する
 * @return {JSX.Element} CTAフォームセクションコンポーネント
 */
export function LpCtaForm() {
  const { ref, isVisible } = useFadeIn();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [formData, setFormData] = useState<FormData>({
    companyName: "",
    name: "",
    email: "",
    phone: "",
    teamSize: "",
  });

  /**
   * フォームフィールドの変更ハンドラ
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e - 変更イベント
   * @return {void}
   */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /**
   * フォーム送信ハンドラ
   * @param {React.FormEvent} e - フォームイベント
   * @return {Promise<void>}
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_ENDPOINT}/api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: formData.companyName,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          teamSize: formData.teamSize || undefined,
          _hp: honeypot || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("送信に失敗しました");
      }

      setIsSubmitted(true);
    } catch {
      setError(lpConfig.ctaForm.errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClassName =
    "w-full px-4 py-3 rounded-lg border border-gray-200 text-[#0F1649] focus:outline-none focus:ring-2 focus:ring-[#1667CF] focus:border-transparent";

  return (
    <section id="cta" className="py-24 lg:py-32 bg-[#02072F]">
      <div
        ref={ref}
        className={cn(
          "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lp-fade-in-up",
          isVisible && "lp-visible",
        )}
      >
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side */}
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight mb-6">
              {lpConfig.ctaForm.heading}
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              {lpConfig.ctaForm.description}
            </p>
            <ul className="space-y-4">
              {lpConfig.ctaForm.trustItems.map(item => (
                <li
                  key={item}
                  className="flex items-center gap-3 text-gray-400"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#30BF78] flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right side - Form */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            {isSubmitted ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-16 h-16 text-[#30BF78] mx-auto mb-6" />
                <p className="text-2xl font-bold text-[#0F1649] mb-2">
                  {lpConfig.ctaForm.successTitle}
                </p>
                <p className="text-gray-600">
                  {lpConfig.ctaForm.successMessage}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot - hidden from users, visible to bots */}
                <input
                  type="text"
                  name="_hp"
                  value={honeypot}
                  onChange={e => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute opacity-0 h-0 w-0 overflow-hidden"
                />

                <div>
                  <label className="text-sm font-bold text-[#0F1649] mb-1 block">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    required
                    value={formData.companyName}
                    onChange={handleChange}
                    className={inputClassName}
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-[#0F1649] mb-1 block">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClassName}
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-[#0F1649] mb-1 block">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClassName}
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-[#0F1649] mb-1 block">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClassName}
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-[#0F1649] mb-1 block">
                    営業担当者数
                  </label>
                  <select
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={handleChange}
                    className={inputClassName}
                  >
                    <option value="">選択してください</option>
                    {lpConfig.ctaForm.teamSizeOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {error && (
                  <p className="text-red-500 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-[#ff8d00] text-white w-full py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {lpConfig.ctaForm.submittingLabel}
                    </>
                  ) : (
                    lpConfig.ctaForm.submitLabel
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
